import { captureException } from '@sentry/react';
import filter from 'lodash/filter';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { bffCreateCartUrl } from 'src/app/client/api';
import { getDeliveryWindows } from 'src/app/client/models/cart';
import ConsumerWebPropTypes from 'src/app/propTypes';
import { checkoutSetCartId, clearResource, segmentTrack } from 'src/app/state/actions';
import * as actionTypes from 'src/app/state/actions/types';
import {
  createGetDeliveryAddressesSelector,
  getAccountDefaultCardId,
  getBFFCartDeliveryAddress,
  getCartItems,
  getCheckoutDeliveryWindows,
  getCheckoutPickupStations,
  getCurrentAccountId,
  getCurrentCartUUID,
  getCurrentUserId,
  getSelectedDeliveryMethodLabelFromCheckout,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type {
  UpdateCartDeliveryAddressRequest,
  UpdateCartDeliveryMethodRequest,
  UpdateCartPaymentMethodRequest,
  UpdateCartTipRequest,
} from 'src/app/types';
import { extractErrorCodes } from 'src/app/utils';
import { DeliveryMethodLabels, DeliveryMethodType, ESI_MAIL_KEY } from 'src/consts';

import { pushNextCheckoutState } from './checkout';

/**
 * # loadCartDeliveryWindows()
 */
export function loadCartDeliveryWindows() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const selectedDeliveryMethodLabel = getSelectedDeliveryMethodLabelFromCheckout(state);
    const { cartId } = checkout;
    const cartUUID = getCurrentCartUUID(state);
    const cartIdentifier = selectedDeliveryMethodLabel === DeliveryMethodLabels.pickUpAtWork ? cartUUID : cartId;
    const getDeliveryAddresses = createGetDeliveryAddressesSelector();
    const deliveryAddresses = getDeliveryAddresses(state);
    const defaultAddress = filter(deliveryAddresses, e => e.attributes.is_default)[0];
    const cartDeliveryAddress = getBFFCartDeliveryAddress(state);

    // check if a delivery address has been saved to persistent state
    // fallback to default address.
    const deliveryAddressId = cartDeliveryAddress?.id || defaultAddress?.id;
    await dispatch(clearResource(ConsumerWebPropTypes.availableDeliveryWindow));
    if (deliveryAddressId) {
      await dispatch(getDeliveryWindows(client, { cartIdentifier, deliveryAddressId }));
    }
  };
}

function setFirstDeliveryWindowAsPickupDeliveryMethod() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const cartUUID = getCurrentCartUUID(state);
    const checkoutDeliveryWindows = getCheckoutDeliveryWindows(state);
    if (checkoutDeliveryWindows?.length > 0 && checkoutDeliveryWindows[0]?.deliveryWindows?.length > 0) {
      const firstDeliveryWindowTime = checkoutDeliveryWindows[0].deliveryWindows[0];
      await dispatch(
        // TODO: type will be changed to pickup once MP accepts it as an option
        updateCartDeliveryMethod({
          type: DeliveryMethodType.pickupStation,
          deliveryWindowId: firstDeliveryWindowTime?.id,
          cartUUID,
        })
      );
      dispatch(segmentTrack(UNIFIED.selectPickupLocation, {}));
    }
  };
}

export function setPickupStationAsDeliveryMethod(selectedPickupStationId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const defaultCardId = getAccountDefaultCardId(state);
    const cartUUID = getCurrentCartUUID(state);
    const pickupStations = getCheckoutPickupStations(state);
    const pickupStationId = selectedPickupStationId || (pickupStations?.length > 0 ? pickupStations[0]?.id : null);
    if (pickupStationId) {
      await dispatch(updateCartDeliveryAddress({ cartUUID, deliveryAddressId: pickupStationId }));
      await dispatch(loadCartDeliveryWindows());
      await dispatch(setFirstDeliveryWindowAsPickupDeliveryMethod());
      // If a default card id is already present, we automatically update the cart with that to save time (from product).
      // The user can select a different card by clicking the payment section in the checkout summary screen.
      if (defaultCardId) await dispatch(updateCartPaymentMethod({ cartUUID, paymentMethodId: defaultCardId }));
      if (selectedPickupStationId) await dispatch(pushNextCheckoutState());
    }
  };
}

export function updateCartDeliveryAddress(body: UpdateCartDeliveryAddressRequest) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const { cartId } = checkout;

    dispatch({ type: actionTypes.FETCH_CART_REQUEST });

    try {
      const response = await client.bffSetCartDeliveryAddress(cartId, body);
      dispatch({ type: actionTypes.FETCH_CART_SUCCESS, data: response?.data });
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_FAILURE, error });
    }
  };
}

export function updateCartDeliveryMethod(body: UpdateCartDeliveryMethodRequest) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const { cartId } = checkout;

    dispatch({ type: actionTypes.FETCH_CART_REQUEST });

    try {
      const response = await client.bffSetCartDeliveryMethod(cartId, body);
      const responseData = response?.data;
      const { key, formattedDeliveryDate } = responseData?.deliveryMethod || {};
      dispatch({ type: actionTypes.FETCH_CART_SUCCESS, data: responseData });
      if (key === ESI_MAIL_KEY)
        dispatch(
          segmentTrack(UNIFIED.selectDeliveryMethod, {
            delivery_method: `${key} (${formattedDeliveryDate})`,
          })
        );
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_FAILURE, error });
    }
  };
}

export function updateCartTip(body: UpdateCartTipRequest) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const { cartId } = checkout;

    dispatch({ type: actionTypes.FETCH_CART_REQUEST });

    try {
      const response = await client.bffSetCartTip(cartId, body);
      dispatch({ type: actionTypes.FETCH_CART_SUCCESS, data: response?.data });
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_FAILURE, error });
    }
  };
}

export function updateCartPaymentMethod(body: UpdateCartPaymentMethodRequest) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const { cartId } = checkout;

    dispatch({ type: actionTypes.FETCH_CART_REQUEST });

    try {
      const response = await client.bffSetCartPayment(cartId, body);
      dispatch({ type: actionTypes.FETCH_CART_SUCCESS, data: response?.data });
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_FAILURE, error });
    }
  };
}

export function loadCartDeliveryMethods() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const { cartId } = checkout;

    dispatch({ type: actionTypes.FETCH_CART_DELIVERY_METHODS_REQUEST });

    try {
      const response = await client.bffGetCartDeliveryMethods(cartId);
      const responseData = response?.data;
      dispatch({ type: actionTypes.FETCH_CART_DELIVERY_METHODS_SUCCESS, data: responseData });
      // Analytics needs the response right away so have to circumvent the traditional redux pattern
      return responseData;
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_DELIVERY_METHODS_FAILURE, error });
    }
  };
}

export function loadCartDeliveryShippingOptions(deliveryMethod: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client, checkout } = state;
    const { cartId } = checkout;
    dispatch({ type: actionTypes.FETCH_CART_DELIVERY_SHIPPING_OPTIONS_REQUEST });

    try {
      const response = await client.bffGetCartDeliveryShippingOptions(cartId, deliveryMethod);
      const responseData = response?.data;
      dispatch({ type: actionTypes.FETCH_CART_DELIVERY_SHIPPING_OPTIONS_SUCCESS, data: responseData });
      return responseData;
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_DELIVERY_SHIPPING_OPTIONS_FAILURE, error });
    }
  };
}

export function bffFetchCart(cartId) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({ type: actionTypes.FETCH_CART_REQUEST });

    try {
      const response = await client.bffGetCart(cartId);
      const responseData = response?.data;
      dispatch({ type: actionTypes.FETCH_CART_SUCCESS, data: responseData });
      dispatch(checkoutSetCartId(responseData.id));
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_CART_FAILURE, error });
    }
  };
}

export function bffCreateCart() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const userId = getCurrentUserId(state);

    const accountId = getCurrentAccountId(state);
    const selectedItems = getCartItems(state);

    const selectedFulfillmentIds = selectedItems.reduce((result, item) => {
      if (item.fulfillmentId) result.push(item.fulfillmentId);
      return result;
    }, []);

    const selectedOtcItemIds = selectedItems.reduce((result, item) => {
      if (item?.otcItemId) result.push({ id: item.otcItemId, quantity: item.quantity });
      return result;
    }, []);

    dispatch({ type: actionTypes.FETCH_CART_REQUEST });
    try {
      const response = await client.bffCreateCart({
        accountId,
        userId,
        prescriptionItemIds: selectedFulfillmentIds,
        otcItems: selectedOtcItemIds,
        source: 'web',
      });
      const responseData = response?.data;
      dispatch({ type: actionTypes.FETCH_CART_SUCCESS, data: responseData });
      dispatch(checkoutSetCartId(responseData?.id));
      return responseData?.id;
    } catch (error) {
      const bffError = error?.error ?? '';
      const api = bffCreateCartUrl();
      const trackErrorPayload = {
        errorMessage: 'Create Cart Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(error),
        source: window.location.href,
      };

      segmentTrack(UNIFIED.error, trackErrorPayload);

      dispatch({ type: actionTypes.FETCH_CART_FAILURE, error });
    }
  };
}

export function bffCheckoutCart({ cartId }: { cartId: string }) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    try {
      const response = await client.bffCheckoutCart(cartId);
      const data = response.data;

      dispatch({ type: actionTypes.CART_CLEAR });
      dispatch({ type: actionTypes.CHECKOUT_CART_SUCCESS, data, orderId: data.id });
      return data;
    } catch (error) {
      captureException(error, {
        tags: {
          feature: 'cart',
        },
        contexts: {
          request: {
            cartId,
          },
        },
      });

      // Temporary measure to have the above functions catch the response error.
      // Eventually will need to refactor to show the error through reducer update

      /* eslint-disable */
      throw { error: error['response']?.data };
    }
  };
}
